import { z } from 'zod';

/**
 * Flow person-picker wire contract — `GET /api/flow/people`.
 *
 * Backs the person value picker (Decision 6): every Clay employee with a
 * `salesforce_user` mirror row, A→Z, so `{me}`-token filters and literal
 * person-name filters share one source of truth for "who can I filter by".
 *
 * No active/inactive filtering (OQ2, resolved): `salesforce_user`
 * (prisma/schema/core.prisma) carries no active/isActive column, so the
 * endpoint returns every mirrored user rather than guessing a filter.
 *
 * Scoped variants (`?scope=`), added for the Owner/ClayDR filter picker:
 * unscoped, this endpoint returns the FULL `salesforce_user` mirror
 * (hundreds of rows) — confirmed bad UX for a field that can only ever hold
 * a small real set of assignees. The business invariant (confirmed by
 * product, not derived from `detectFlowRole` — see `src/shared/flow/role.ts`,
 * a fragile heuristic deliberately NOT used here): an Account can only be
 * owned by a GTME or a GS, and an Account's ClayDR field can only ever hold
 * a ClayDR. That's guaranteed by who actually appears as
 * `salesforce_account.sf_owner_id` / `.clay_dr_id` in the data — not a role
 * lookup. `account_owners` and `account_claydrs` each scope the directory to
 * exactly the users who appear in the corresponding column, so the picker
 * offers only names that can legitimately be assigned.
 */
export const FlowPersonSchema = z.object({
  /** salesforce_user.id — the id compared against `idSql` when a `{me}` token resolves. */
  sfUserId: z.string(),
  name: z.string(),
});
export type FlowPerson = z.infer<typeof FlowPersonSchema>;

export const FlowPeopleResponseSchema = z.object({
  people: z.array(FlowPersonSchema),
});
export type FlowPeopleResponse = z.infer<typeof FlowPeopleResponseSchema>;

/**
 * `?scope=` values for `GET /api/flow/people` — see the module doc comment
 * above for the business invariant each one narrows to.
 */
export const FLOW_PEOPLE_SCOPES = ['account_owners', 'account_claydrs'] as const;
export const FlowPeopleScopeSchema = z.enum(FLOW_PEOPLE_SCOPES);
export type FlowPeopleScope = z.infer<typeof FlowPeopleScopeSchema>;
